import React, { useEffect, useState } from 'react';

// constellation
import { Box, LoadingMask, Text } from '@zillow/constellation-10';

// components
import PriceMyRentalComponent from './component';
import NoZestimateModal from '../NoZestimateModal';

// flux
import { getAddressAndComparables } from '../flux/actions/AddressActions';
import { setAppValues } from '../flux/actions/AppActions';
import { useAppDispatch } from '../flux/configureStore';
import { handleFilterChange, resetFilters } from '../flux/actions/FilterActions';
import constants from '../flux/constants';

// misc/utils
import { augmentError, formatForLog } from '../utils/errors';
import { logger } from '../index';
import ErrorConstants from '../constants/ErrorConstants';

// types
import type { PartialFilterType } from '../models/Types';
import type { PropsWithAPIParams } from '.';

const PriceMyRentalWidget: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<PropsWithAPIParams>>
> = ({ street, unit, city, state, zip, screenWidth, isMobile, googleMapsApiKey, heading }) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [isNoZestModalOpen, openNoZestModal] = useState(false);
  const [appError, handleSetAppError] = React.useState('');

  useEffect(() => {
    const setFilter = async (partialFilterObj?: PartialFilterType) => dispatch(handleFilterChange(partialFilterObj));
    dispatch({
      type: constants.INIT_SET_FILTER,
      payload: setFilter,
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [street, unit, city, state, zip]);

  useEffect(() => {
    dispatch(setAppValues({ device: { screenWidth, isMobile }, googleMapsApiKey }));
  }, [screenWidth, isMobile, googleMapsApiKey]);

  useEffect(() => {
    handleSetAppError('');
    dispatch(resetFilters());
    fetchData(formattedAddress);
  }, [street, unit, city, state, zip]);

  const formattedAddress = {
    street: street,
    city: city,
    state: state,
    zip: zip,
    unit: unit ? unit : '',
  };

  const fetchData = async (formattedAddress: {
    street: string;
    city: string;
    state: string;
    zip: string;
    unit: string;
  }) => {
    try {
      await dispatch(getAddressAndComparables(formattedAddress));
    } catch (error) {
      handleSetAppError(error.errorMsg);
      if (error.errorCode === ErrorConstants.RENT_ZESTIMATE_NOT_FOUND) {
        openNoZestModal(true);
      }

      // handle the case where no address is returned
      augmentError(error, {
        root: 'components/PriceMyRental#getAddressAndComparables',
      });
      logger.error(formatForLog(error), 'Error fetching address and comparables');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseNoZestModal = () => {
    openNoZestModal(false);
  };

  if (isLoading) {
    return (
      <LoadingMask>
        <Box css={{ height: '100px', width: '100px' }} />
      </LoadingMask>
    );
  } else if (isNoZestModalOpen) {
    return (
      <NoZestimateModal onClose={handleCloseNoZestModal} onSetAppError={handleSetAppError} isOpen={isNoZestModalOpen} />
    );
  } else if (appError) {
    return <Text textStyle="heading-xs">{appError}</Text>;
  } else {
    return <PriceMyRentalComponent heading={heading} />;
  }
};

export default PriceMyRentalWidget;
